<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { Link } from 'svelte-routing';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';

  export let brand = 'it'; 
  export let brandDisplayName = 'I.T Fashion';

  let promotions = [];
  let isLoading = true;
  let error = '';

  // Pagination State
  let currentPage = 1;
  const pageSize = 10;
  
  // Reactively calculate the items for the current page and total pages
  $: paginatedPromotions = promotions.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  $: totalPages = Math.ceil(promotions.length / pageSize) || 1;

  onMount(async () => {
      fetchBrandPromotions();
  });

  async function fetchBrandPromotions() {
      isLoading = true;
      try {
          // Fetch a larger pool of promotions to paginate locally
          const res = await fetch(getApiUrl(`/promotions/search?brand=${brand}&countryCode=${$selectedCountry}&pageSize=100`));
          
          if (!res.ok) throw new Error($t('partner.fetch_error') || "Failed to load partner promotions.");
          
          const result = await res.json();
          let fetchedPromos = result.data || result || [];
          
          promotions = fetchedPromos.sort((a, b) => {
              const dateA = new Date(a.startDate || a.createdAt || 0);
              const dateB = new Date(b.startDate || b.createdAt || 0);
              return dateB - dateA;
          });

      } catch (e) {
          error = e.message;
      } finally {
          isLoading = false;
      }
  }

  function nextPage() {
      if (currentPage < totalPages) currentPage++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function prevPage() {
      if (currentPage > 1) currentPage--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
  }
</script>

<div class="partner-container">
  <!-- Brand Header & Logo -->
  <div class="brand-header">
      <div class="logo-placeholder">
          <h1>{brandDisplayName}</h1>
      </div>
      <p class="tagline">{$t('partner.tagline') || 'Exclusive partner offers for Snapperk members'}</p>
  </div>

  {#if isLoading}
      <div class="loading">{$t('partner.loading_collections') || 'Loading latest collections...'}</div>
  {:else if error}
      <div class="error">{error}</div>
  {:else if promotions.length === 0}
      <div class="no-data">{($t('partner.no_offers') || 'No current offers available for {brand}.').replace('{brand}', brandDisplayName)}</div>
  {:else}
      <!-- Minimalist Grid (Paginated) -->
      <div class="promo-grid">
          {#each paginatedPromotions as promo}
              <Link to={`/${brand}/promo/${promo._id || promo.id}`} class="promo-card">
                  <div class="image-wrapper">
                      <img 
                          src={Array.isArray(promo.images) && promo.images.length > 0 ? promo.images[0] : (promo.imageUrl || '/placeholder.png')} 
                          alt={promo.name} 
                          on:error={(e) => e.target.src = '/public/placeholder.png'}
                      />
                  </div>
                  <div class="card-content">
                      <h3 class="promo-name">{promo.name}</h3>
                  </div>
              </Link>
          {/each}
      </div>

      <!-- Pagination Controls -->
      {#if totalPages > 1}
          <div class="pagination">
              <button class="page-btn" disabled={currentPage === 1} on:click={prevPage}>&larr; Prev</button>
              <span class="page-info">Page {currentPage} of {totalPages}</span>
              <button class="page-btn" disabled={currentPage === totalPages} on:click={nextPage}>Next &rarr;</button>
          </div>
      {/if}
  {/if}
</div>

<style>
  .partner-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 2rem 1rem;
      background-color: #fafafa;
      min-height: 100vh;
  }

  .brand-header {
      text-align: center;
      margin-bottom: 3rem;
  }

  .logo-placeholder h1 {
      font-size: 3rem;
      font-weight: 900;
      letter-spacing: 2px;
      margin: 0;
      color: #000;
      text-transform: uppercase;
  }

  .tagline {
      color: #666;
      font-size: 1.1rem;
      margin-top: 0.5rem;
      font-style: italic;
  }

  .promo-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 2rem;
  }

  .promo-card {
      background: #fff;
      border-radius: 0;
      overflow: hidden;
      text-decoration: none;
      color: inherit;
      box-shadow: 0 4px 10px rgba(0,0,0,0.03);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      display: flex;
      flex-direction: column;
  }

  .promo-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0,0,0,0.08);
  }

  .image-wrapper {
      width: 100%;
      aspect-ratio: 3/4;
      overflow: hidden;
      background: #eee;
  }

  .image-wrapper img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
  }

  .promo-card:hover .image-wrapper img {
      transform: scale(1.05);
  }

  .card-content {
      padding: 1.2rem;
      text-align: center;
  }

  .promo-name {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #111;
      line-height: 1.4;
  }

  .loading, .error, .no-data {
      text-align: center;
      padding: 3rem;
      font-size: 1.2rem;
      color: #555;
  }

  /* Pagination Styles */
  .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1.5rem;
      margin-top: 3rem;
      padding-top: 2rem;
      border-top: 1px solid #eaeaea;
  }

  .page-btn {
      background: #000;
      color: #fff;
      border: none;
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
      font-weight: bold;
      cursor: pointer;
      transition: background 0.2s ease, opacity 0.2s ease;
  }

  .page-btn:hover:not(:disabled) {
      background: #333;
  }

  .page-btn:disabled {
      background: #ccc;
      cursor: not-allowed;
      opacity: 0.7;
  }

  .page-info {
      font-size: 1.1rem;
      font-weight: 500;
      color: #333;
  }
</style>